import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import AiLookResults, { productImageUrl } from './AiLookResults';
import { useAuth } from './AuthContext';
import LookShareCard from './LookShareCard';
import PhotoViewer from './PhotoViewer';
import ProductInfoDialog from './ProductInfoDialog';
import SimilarProductRail from './SimilarProductRail';
import SocialShareButtons from './SocialShareButtons';
import { formatProductPriceEur, priceAmountToEur, selectedProductsTotalEur } from './currency';
import { shortDisplayTitle } from './displayText';
import { setPageMeta } from './pageMeta';
import useModalEscape from './useModalEscape';

const fallbackImage =
  'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?auto=format&fit=crop&w=1400&q=80';

function EventDetailSkeleton({ t }) {
  return (
    <div className="ct-detail-layout">
      <article className="ct-detail-card ct-detail-skeleton" aria-busy="true">
        <div className="ct-detail-media">
          <span className="ct-skeleton-block"></span>
        </div>
        <div className="ct-detail-content">
          <div className="ct-post-head no-padding">
            <span className="ct-avatar small ct-skeleton-block"></span>
            <div>
              <span className="ct-skeleton-line medium"></span>
              <span className="ct-skeleton-line short"></span>
            </div>
          </div>
          <span className="ct-skeleton-line title"></span>
          <span className="ct-skeleton-line"></span>
          <span className="ct-skeleton-line medium"></span>
          <div className="ct-similar-rail ct-similar-rail-empty">
            <div>
              <p className="ct-kicker">{t('similarRail.kicker')}</p>
              <h2>{t('similarRail.title')}</h2>
              <span>{t('similarRail.loading')}</span>
            </div>
            <span className="ct-input-spinner"></span>
          </div>
        </div>
      </article>
    </div>
  );
}

function avatarImageUrl(url) {
  if (!url) return '';
  const value = String(url);
  if (value.startsWith('/media/') || value.startsWith('/outfit-detective/') || value.includes('/outfit-detective/')) return value;
  return `/api/media/avatar?url=${encodeURIComponent(url)}`;
}

function isPendingTryOn(item) {
  return item?.status === 'queued' || item?.status === 'running';
}

const EventDetail = () => {
  const { language, locale, t } = useI18n();
  const { isAuthenticated, requestLogin } = useAuth();
  const { id } = useParams();
  const location = useLocation();
  const [event, setEvent] = useState(null);
  const [aiMatch, setAiMatch] = useState(null);
  const [aiStatus, setAiStatus] = useState('idle');
  const [aiError, setAiError] = useState('');
  const [aiStale, setAiStale] = useState(false);
  const [userPhotos, setUserPhotos] = useState([]);
  const [selectedPhotoId, setSelectedPhotoId] = useState('');
  const [tryOnStatus, setTryOnStatus] = useState('idle');
  const [tryOnPreview, setTryOnPreview] = useState(null);
  const [tryOnError, setTryOnError] = useState('');
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [tryOnSuccessPreview, setTryOnSuccessPreview] = useState(null);
  const [lookShareOpen, setLookShareOpen] = useState(false);
  const [eventTryOns, setEventTryOns] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [tryOnType, setTryOnType] = useState('selected_products');
  const [selectedProductByItem, setSelectedProductByItem] = useState({});
  const [activeCartPreset, setActiveCartPreset] = useState('closest');
  const [tryOnProductInfo, setTryOnProductInfo] = useState(null);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const aiSectionRef = useRef(null);
  const announcedTryOnPreviewIdsRef = useRef(new Set());
  const tryOnStatusByIdRef = useRef(new Map());
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }),
    [locale],
  );

  const openTryOn = useCallback(() => {
    if (!isAuthenticated) {
      requestLogin({
        title: t('authPrompt.tryOnTitle'),
        copy: t('authPrompt.tryOnCopy'),
        afterLogin: () => setTryOnOpen(true),
      });
      return;
    }
    setTryOnOpen(true);
  }, [isAuthenticated, requestLogin, t]);

  useEffect(() => {
    let mounted = true;

    async function loadEvent() {
      setLoading(true);
      setError('');
      setAiMatch(null);
      setAiStatus('idle');
      setAiError('');
      setSelectedProductByItem({});
      setActiveCartPreset('closest');

      try {
        const [eventRes, aiRes, photosRes] = await Promise.all([
          api.getEvent(id),
          api.getEventAiMatch(id, language).catch(() => ({ data: null })),
          isAuthenticated ? api.getUserPhotos().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        if (!mounted) return;
        setEvent(eventRes.data);
        setAiStale(Boolean(aiRes.stale));
        if (aiRes.status === 'completed' && aiRes.data) {
          setAiMatch(aiRes.data);
          setAiStatus('ready');
        } else if (aiRes.status === 'running') {
          if (aiRes.data) setAiMatch(aiRes.data);
          setAiStatus('loading');
        } else if (aiRes.status === 'failed') {
          setAiStatus('error');
          setAiError(aiRes.error || aiRes.run?.error_message || 'ai_matching_failed');
        }
        const photos = photosRes.data || [];
        setUserPhotos(photos);
        setSelectedPhotoId((current) => current || String(photos.find((photo) => photo.is_default)?.id || photos[0]?.id || ''));
      } catch (err) {
        if (!mounted) return;
        setError(err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadEvent();

    return () => {
      mounted = false;
    };
  }, [id, isAuthenticated, language]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tryon') !== '1') return;
    openTryOn();
  }, [location.search, openTryOn]);

  useEffect(() => {
    if (!tryOnOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [tryOnOpen]);

  useModalEscape(tryOnOpen, () => setTryOnOpen(false));
  useModalEscape(Boolean(tryOnProductInfo), () => setTryOnProductInfo(null));

  const showTryOnSuccess = useCallback((preview) => {
    if (!preview?.id || announcedTryOnPreviewIdsRef.current.has(preview.id)) return;
    announcedTryOnPreviewIdsRef.current.add(preview.id);
    setTryOnSuccessPreview(preview);
  }, []);

  const rememberTryOnStatuses = useCallback((items, options = {}) => {
    (items || []).forEach((item) => {
      if (!item?.id) return;
      const previousStatus = tryOnStatusByIdRef.current.get(item.id);
      if (options.announceReady && isPendingTryOn({ status: previousStatus }) && item.status === 'completed') {
        showTryOnSuccess(item);
      }
      tryOnStatusByIdRef.current.set(item.id, item.status);
    });
  }, [showTryOnSuccess]);

  useEffect(() => {
    if (!tryOnOpen) return undefined;
    let cancelled = false;

    async function loadEventTryOns() {
      if (!isAuthenticated) return;
      try {
        const payload = await api.getTryOnPreviews({ eventId: id });
        if (!cancelled) {
          const previews = payload.data || [];
          rememberTryOnStatuses(previews, { announceReady: true });
          setEventTryOns(previews);
        }
      } catch {
        if (!cancelled) setEventTryOns([]);
      }
    }

    loadEventTryOns();
    const timer = window.setInterval(() => {
      if (eventTryOns.some(isPendingTryOn)) loadEventTryOns();
    }, 6000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [tryOnOpen, id, eventTryOns, isAuthenticated, rememberTryOnStatuses]);

  async function runAiMatch(options = {}) {
    if (!isAuthenticated) {
      requestLogin({
        title: t('authPrompt.matchTitle'),
        copy: t('authPrompt.matchCopy'),
        afterLogin: () => runAiMatch(options),
      });
      return;
    }
    setAiStatus('loading');
    setAiError('');
    setAiStale(false);

    try {
      const payload = await api.matchEventLook(id, language, { force: options.force === true });
      setAiStale(Boolean(payload.stale));
      if (payload.status === 'completed') {
        setAiMatch(payload.data || null);
        setAiStatus('ready');
        const eventPayload = await api.getEvent(id).catch(() => null);
        if (eventPayload?.data) setEvent(eventPayload.data);
      } else if (payload.status === 'running') {
        if (payload.data) setAiMatch(payload.data);
        setAiStatus('loading');
      } else if (payload.status === 'failed') {
        setAiError(payload.error || payload.run?.error_message || 'ai_matching_failed');
        setAiStatus('error');
      }
    } catch (err) {
      setAiError(err.message || 'ai_matching_failed');
      setAiStatus('error');
    }
  }

  function scrollToAiSection() {
    window.setTimeout(() => {
      aiSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }

  function showAllAiMatches() {
    scrollToAiSection();
    if (aiStatus === 'idle') runAiMatch();
  }

  function findCartProductsFromTryOn() {
    setTryOnOpen(false);
    scrollToAiSection();
    if (aiStatus !== 'loading') runAiMatch({ force: aiStatus === 'ready' });
  }

  function refreshAiMatch() {
    runAiMatch({ force: true });
  }

  useEffect(() => {
    if (aiStatus !== 'loading' || !id) return undefined;
    let cancelled = false;

    const timer = window.setInterval(async () => {
      try {
        const payload = await api.getEventAiMatch(id, language);
        if (cancelled) return;
        setAiStale(Boolean(payload.stale));
        if (payload.status === 'running' && payload.data) {
          setAiMatch(payload.data);
        } else if (payload.status === 'completed') {
          setAiMatch(payload.data || null);
          setAiStatus('ready');
          setAiStale(Boolean(payload.stale));
          const eventPayload = await api.getEvent(id).catch(() => null);
          if (!cancelled && eventPayload?.data) setEvent(eventPayload.data);
        } else if (payload.status === 'failed') {
          setAiError(payload.error || payload.run?.error_message || 'ai_matching_failed');
          setAiStatus('error');
          setAiStale(Boolean(payload.stale));
        }
      } catch {
        // Keep polling silently while the background job is expected to continue.
      }
    }, 4500);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [aiStatus, id, language]);

  async function runTryOn() {
    if (!selectedPhotoId) return;
    if (eventTryOns.some(isPendingTryOn) || ['loading', 'queued'].includes(tryOnStatus)) return;
    const selectedProductIds = getSelectedProductIds();
    if (tryOnType === 'selected_products' && selectedProductIds.length === 0) {
      setTryOnError('selected_products_required');
      setTryOnStatus('error');
      return;
    }
    setTryOnStatus('loading');
    setTryOnError('');
    setTryOnPreview(null);
    setTryOnSuccessPreview(null);

    try {
      const payload = await api.createTryOnPreview(id, Number(selectedPhotoId), language, {
        notifyEmail,
        tryOnType,
        selectedProductIds,
      });
      setTryOnPreview(payload.data || null);
      setEventTryOns((current) => {
        const next = payload.data;
        if (!next?.id) return current;
        return [next, ...current.filter((item) => item.id !== next.id)];
      });
      if (payload.data?.id) tryOnStatusByIdRef.current.set(payload.data.id, payload.data.status);
      if (payload.data?.status === 'completed') {
        setTryOnStatus('ready');
        showTryOnSuccess(payload.data);
      } else {
        setTryOnStatus('queued');
      }
    } catch (err) {
      setTryOnError(err.message || 'try_on_failed');
      setTryOnStatus('error');
    }
  }

  function formatPrice(candidate) {
    return formatProductPriceEur(candidate, currencyFormatter, t('detail.priceUnavailable'));
  }

  const candidatePoolForItem = useCallback((item) => {
    return ['exact', 'similar', 'budget', 'luxury']
      .flatMap((lane) => (item.candidates?.[lane] || [])
        .filter((candidate) => String(candidate?.image_url || '').trim())
        .slice(0, 3)
        .map((candidate) => ({ ...candidate, lane })));
  }, []);

  const candidateMatchScore = useCallback((candidate) => {
    const values = [
      candidate?.score,
      candidate?.match_score,
      candidate?.visual_score,
      candidate?.similarity_score,
    ].map(Number);
    const score = values.find(Number.isFinite);
    return Number.isFinite(score) ? score : 0;
  }, []);

  const bestCandidateForItem = useCallback((item) => {
    const lanePriority = {
      exact: 4,
      similar: 3,
      budget: 2,
      luxury: 1,
    };
    return candidatePoolForItem(item)
      .filter((candidate) => candidate?.id)
      .sort((a, b) => {
        const scoreDelta = candidateMatchScore(b) - candidateMatchScore(a);
        if (Math.abs(scoreDelta) > 0.001) return scoreDelta;
        return (lanePriority[b.lane] || 0) - (lanePriority[a.lane] || 0);
      })[0] || null;
  }, [candidateMatchScore, candidatePoolForItem]);

  function presetCandidateForItem(item, preset) {
    if (preset === 'closest') return bestCandidateForItem(item);
    const preferredLane = preset === 'luxury' ? 'luxury' : 'budget';
    const laneCandidates = candidatePoolForItem(item)
      .filter((candidate) => candidate.lane === preferredLane && candidate?.id)
      .sort((a, b) => {
        const scoreDelta = candidateMatchScore(b) - candidateMatchScore(a);
        if (Math.abs(scoreDelta) > 0.001) return scoreDelta;
        const aPrice = priceAmountToEur(a.price_amount, a.currency);
        const bPrice = priceAmountToEur(b.price_amount, b.currency);
        if (aPrice !== null && bPrice !== null) {
          return preset === 'luxury' ? bPrice - aPrice : aPrice - bPrice;
        }
        return 0;
      });
    return laneCandidates[0] || bestCandidateForItem(item);
  }

  function applyCartPreset(preset) {
    setTryOnType('selected_products');
    setTryOnError('');
    setTryOnStatus((current) => (current === 'error' ? 'idle' : current));
    setActiveCartPreset(preset);
    const next = {};
    (aiMatch?.items || []).forEach((item) => {
      const candidate = presetCandidateForItem(item, preset);
      if (candidate?.id) next[String(item.id)] = Number(candidate.id);
    });
    setSelectedProductByItem(next);
  }

  function getSelectedProductIds() {
    return Object.values(selectedProductByItem).filter(Boolean).map(Number);
  }

  const hasCandidateProducts = useCallback((match) => {
    return (match?.items || []).some((item) => candidatePoolForItem(item).length > 0);
  }, [candidatePoolForItem]);

  function selectedCandidates() {
    const ids = new Set(getSelectedProductIds());
    return (aiMatch?.items || [])
      .flatMap(candidatePoolForItem)
      .filter((candidate) => ids.has(Number(candidate.id)));
  }

  function selectedTotal() {
    const picks = selectedCandidates();
    const prices = picks
      .map((candidate) => priceAmountToEur(candidate.price_amount, candidate.currency))
      .filter((price) => price !== null);
    if (prices.length === 0) return null;
    return {
      amount: prices.reduce((sum, price) => sum + price, 0),
      currency: 'EUR',
    };
  }

  function aiMatchTotal() {
    const picks = (aiMatch?.items || [])
      .map((item) => candidatePoolForItem(item)[0])
      .filter(Boolean);
    const prices = picks
      .map((candidate) => priceAmountToEur(candidate.price_amount, candidate.currency))
      .filter((price) => price !== null);
    if (prices.length === 0) return null;
    return {
      amount: prices.reduce((sum, price) => sum + price, 0),
      currency: 'EUR',
    };
  }

  function formatMoney(amount, currency) {
    const value = Number(amount);
    if (!Number.isFinite(value)) return t('tryon.totalUnavailable');
    return currencyFormatter.format(value);
  }

  function previewTotalLabel(preview) {
    const total = selectedProductsTotalEur(preview?.selected_products || []);
    if (total === null) return t('tryon.totalUnavailable');
    return currencyFormatter.format(total);
  }

  function toggleSelectedProduct(itemId, candidateId) {
    setTryOnError('');
    setTryOnStatus((current) => (current === 'error' ? 'idle' : current));
    setActiveCartPreset('');
    setSelectedProductByItem((current) => {
      const key = String(itemId);
      if (String(current[key]) === String(candidateId)) {
        const next = { ...current };
        delete next[key];
        return next;
      }
      return { ...current, [key]: Number(candidateId) };
    });
  }

  function openTryOnProductInfo(item, candidate) {
    setTryOnProductInfo({
      ...candidate,
      itemId: item.id,
      itemCategory: item.category,
    });
  }

  function getTryOnPreviewShareData() {
    const celebrity = event.celebrity_name || t('tryonShare.celebrityFallback');
    const pathname = tryOnPreview?.public_share_id ? `/t/${tryOnPreview.public_share_id}` : `/events/${event.id}`;
    const url = new URL(pathname, window.location.origin).href;
    const isShopTryOn = tryOnPreview?.try_on_type === 'selected_products';
    const total = previewTotalLabel(tryOnPreview);
    return {
      celebrity,
      url,
      title: isShopTryOn ? t('tryonShare.shopTitle', { celebrity, total }) : t('tryonShare.title', { celebrity }),
      text: isShopTryOn ? t('tryonShare.shopText', { celebrity, total }) : t('tryonShare.text', { celebrity }),
      media: tryOnPreview?.image_url,
    };
  }

  function getLookShareData() {
    const celebrity = event.celebrity_name || t('tryonShare.celebrityFallback');
    const url = new URL(`/events/${event.id}`, window.location.origin).href;
    return {
      url,
      title: t('shareLook.title', { celebrity }),
      text: t('shareLook.text', { celebrity }),
      media: event.image_url,
    };
  }

  function sourceLabel() {
    return event.source_name || t('copyright.sourceFallback');
  }

  useEffect(() => {
    if (!tryOnPreview?.id || !['queued', 'loading'].includes(tryOnStatus)) return undefined;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const payload = await api.getTryOnPreview(tryOnPreview.id);
        if (cancelled) return;
        setTryOnPreview(payload.data || null);
        setEventTryOns((current) => current.map((item) => (item.id === payload.data?.id ? payload.data : item)));
        if (payload.data?.id) tryOnStatusByIdRef.current.set(payload.data.id, payload.data.status);
        if (payload.data?.status === 'completed') {
          setTryOnStatus('ready');
          showTryOnSuccess(payload.data);
        }
        if (payload.data?.status === 'failed') {
          setTryOnError(payload.data.error_message || 'try_on_failed');
          setTryOnStatus('error');
        }
      } catch {
        // Keep the queued state; the profile page can still show the final result.
      }
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [tryOnPreview?.id, tryOnStatus, showTryOnSuccess]);

  useEffect(() => {
    if (!tryOnOpen || tryOnType !== 'selected_products' || aiStatus !== 'ready') return;
    setSelectedProductByItem((current) => {
      if (Object.keys(current).length > 0) return current;
      const next = {};
      (aiMatch?.items || []).forEach((item) => {
        const best = bestCandidateForItem(item);
        if (best?.id) next[String(item.id)] = Number(best.id);
      });
      return Object.keys(next).length > 0 ? next : current;
    });
  }, [tryOnOpen, tryOnType, aiStatus, aiMatch, bestCandidateForItem]);

  useEffect(() => {
    if (!tryOnOpen) return;
    if (tryOnType === 'selected_products' && !hasCandidateProducts(aiMatch)) {
      setTryOnType('replica');
      setTryOnError('');
      setTryOnStatus((current) => (current === 'error' ? 'idle' : current));
    }
  }, [tryOnOpen, tryOnType, aiMatch, hasCandidateProducts]);

  useEffect(() => {
    if (!event) return;
    const title = [event.celebrity_name, event.title].filter(Boolean).join(' - ') || t('tryonShare.celebrityFallback');
    setPageMeta({
      title: `${shortDisplayTitle(title, 72)} | Outfit Detective`,
      description: event.description || t('detail.aiEmptyCopy'),
      pathname: `/events/${event.id || id}`,
      image: event.image_url || fallbackImage,
      robots: 'index,follow',
    });
  }, [event, id, t]);

  if (loading) return <EventDetailSkeleton t={t} />;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!event) return null;

  const pendingTryOn = eventTryOns.find(isPendingTryOn) || (['loading', 'queued'].includes(tryOnStatus) ? tryOnPreview : null);
  const hasPendingTryOn = Boolean(pendingTryOn) || ['loading', 'queued'].includes(tryOnStatus);
  const pendingEmailEnabled = pendingTryOn?.notify_email ?? notifyEmail;
  const pickedProducts = selectedCandidates();
  const pickedTotal = selectedTotal();
  const canBuildCartFit = aiStatus === 'ready' && hasCandidateProducts(aiMatch);
  const canGenerateSelectedProducts = tryOnType !== 'selected_products' || pickedProducts.length > 0;
  const storyTotal = aiMatchTotal();
  const mainImageUrl = event.image_url || fallbackImage;

  return (
    <div className="ct-detail-layout">
      <article className="ct-detail-card">
        <div className="ct-detail-media">
          <button
            type="button"
            className="ct-image-viewer-trigger ct-detail-media-trigger"
            onClick={() => setPhotoViewerOpen(true)}
            aria-label={t('tryonGallery.viewerLabel')}
          >
            <img src={mainImageUrl} alt={event.title} />
          </button>
        </div>
        <div className="ct-detail-content">
          <div className="ct-post-head no-padding">
            <Link to={`/following/${event.celebrity_slug}`} className="ct-avatar">
              {event.celebrity_image_url ? (
                <img
                  src={avatarImageUrl(event.celebrity_image_url)}
                  alt={event.celebrity_name}
                  onError={(e) => {
                    e.currentTarget.replaceWith(document.createTextNode((event.celebrity_name || '?').slice(0, 1).toUpperCase()));
                  }}
                />
              ) : (
                (event.celebrity_name || '?').slice(0, 1).toUpperCase()
              )}
            </Link>
            <div>
              <Link to={`/following/${event.celebrity_slug}`}>
                <strong>{event.celebrity_name}</strong>
              </Link>
              <span>{event.source_name} · {event.event_type?.replaceAll('_', ' ')}</span>
            </div>
          </div>
          <h1 className="ct-detail-title-short" title={event.title}>{shortDisplayTitle(event.title)}</h1>
          {event.description ? <p>{event.description}</p> : null}
          <div className="ct-content-credit detail">
            {event.source_url ? (
              <a href={event.source_url} target="_blank" rel="noreferrer">
                {t('copyright.source', { source: sourceLabel() })}
              </a>
            ) : (
              <span>{t('copyright.source', { source: sourceLabel() })}</span>
            )}
            <small>{t('copyright.preview')}</small>
            <Link to="/contact">{t('copyright.takedown')}</Link>
          </div>

          <div className="ct-detail-tryon-cta">
            <span>
              <i className="ph ph-magic-wand"></i>
            </span>
            <div>
              <strong>{t('tryon.ctaTitle')}</strong>
              <em>{t('tryon.ctaCopy')}</em>
            </div>
            <button type="button" onClick={openTryOn}>
              {t('tryon.open')}
            </button>
          </div>

          <SimilarProductRail
            match={aiMatch}
            status={aiStatus}
            onStart={() => runAiMatch()}
            onShowAll={showAllAiMatches}
            onTryOn={openTryOn}
            formatPrice={formatPrice}
            t={t}
          />

          <div className="ct-detail-share-card">
            <div>
              <p className="ct-kicker">{t('shareLook.kicker')}</p>
              <strong>{t('shareLook.cardTitle')}</strong>
              <span>{t('shareLook.cardCopy')}</span>
            </div>
            <SocialShareButtons
              {...getLookShareData()}
              label={t('shareLook.button')}
            />
          </div>

        </div>
      </article>

      <section className="ct-detail-ai-section" ref={aiSectionRef}>
        <div className="ct-detail-ai-head">
          <div>
            <p className="ct-kicker">{t('aiDialog.kicker')}</p>
            <h2>{t('detail.aiTitle')}</h2>
          </div>
          {aiStatus === 'idle' ? (
            <button type="button" className="ct-detail-ai-button" onClick={() => runAiMatch()}>
              <i className="ph ph-sparkle"></i>
              {t('feed.aiAction')}
            </button>
          ) : aiStatus === 'ready' ? (
            <div className="ct-detail-ai-actions">
              <button type="button" className="ct-detail-ai-button" onClick={() => setLookShareOpen(true)}>
                <i className="ph ph-frame-corners"></i>
                {t('lookShare.open')}
              </button>
              <button type="button" className="ct-detail-ai-button secondary" onClick={refreshAiMatch}>
                <i className="ph ph-arrow-clockwise"></i>
                {aiStale ? t('detail.refreshAi') : t('aiDialog.retry')}
              </button>
            </div>
          ) : null}
        </div>
        {aiStatus === 'idle' ? (
          <div className="ct-detail-ai-empty">
            <i className="ph ph-shopping-bag-open"></i>
            <strong>{t('detail.aiEmptyTitle')}</strong>
            <span>{t('detail.aiEmptyCopy')}</span>
          </div>
        ) : (
          <AiLookResults
            status={aiStatus}
            result={aiMatch}
            error={aiError}
            context="page"
            onRetry={refreshAiMatch}
            onTryOn={openTryOn}
          />
        )}
      </section>

      {tryOnOpen ? (
        <div className="ct-tryon-overlay" role="presentation" onMouseDown={() => setTryOnOpen(false)}>
          <section
            className="ct-tryon-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ct-tryon-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button type="button" className="ct-ai-close" onClick={() => setTryOnOpen(false)} aria-label={t('tryon.hide')}>
              <i className="ph ph-x"></i>
            </button>
            <div className="ct-detail-ai-head">
              <div>
                <p className="ct-kicker">{t('tryon.kicker')}</p>
                <h2 id="ct-tryon-title">{t('tryon.title')}</h2>
                <p>{t('tryon.copy')}</p>
              </div>
              <button
                type="button"
                className="ct-tryon-original-thumb"
                onClick={() => setPhotoViewerOpen(true)}
                aria-label={t('tryonGallery.viewerLabel')}
              >
                <img src={mainImageUrl} alt="" />
                <span>{t('tryon.originalPost')}</span>
              </button>
            </div>
            {userPhotos.length === 0 ? (
              <div className="ct-detail-ai-empty">
                <i className="ph ph-user-square"></i>
                <strong>{t('tryon.noPhotosTitle')}</strong>
                <span>{t('tryon.noPhotosCopy')}</span>
                <Link to="/photos" className="ct-detail-ai-button" onClick={() => setTryOnOpen(false)}>
                  {t('photos.title')}
                </Link>
              </div>
            ) : (
              <div className="ct-tryon-panel">
                {eventTryOns.length > 0 ? (
                  <section className="ct-existing-tryons">
                    <div>
                      <p className="ct-kicker">{t('tryon.existingKicker')}</p>
                      <h3>{t('tryon.existingTitle')}</h3>
                      <p>{hasPendingTryOn ? t('tryon.pendingExistingCopy') : t('tryon.existingCopy')}</p>
                    </div>
                    <div className="ct-existing-tryon-list">
                      {eventTryOns.map((item) => (
                        <article className="ct-existing-tryon-card" key={item.id}>
                          <span className="ct-existing-tryon-image">
                            {item.image_url ? (
                              <img src={item.image_url} alt={t('tryon.resultAlt')} />
                            ) : (
                              <i className={item.status === 'failed' ? 'ph ph-warning-circle' : 'ph ph-hourglass-medium'}></i>
                            )}
                          </span>
                          <div>
                            <strong>{t(`tryonGallery.status.${item.status}`)}</strong>
                            <span>{item.user_photo?.title || t('tryonGallery.userPhoto')}</span>
                            {item.event?.image_url ? (
                              <em>
                                <img src={item.event.image_url} alt="" />
                                {t('tryon.originalPost')}
                              </em>
                            ) : null}
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                ) : null}
                {!hasPendingTryOn ? (
                  <>
                    <div className={`ct-tryon-mode-grid${canBuildCartFit ? '' : ' single'}`} role="group" aria-label={t('tryon.modeLabel')}>
                      {canBuildCartFit ? (
                        <button
                          type="button"
                          className={tryOnType === 'selected_products' ? 'active' : ''}
                          onClick={() => {
                            setTryOnType('selected_products');
                            setTryOnError('');
                            setTryOnStatus((current) => (current === 'error' ? 'idle' : current));
                          }}
                        >
                          <span>{t('tryon.modeTryonTitle')}</span>
                          <strong>{t('tryon.modeTryonName')}</strong>
                          <em>{t('tryon.modeTryonCopy')}</em>
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={tryOnType === 'replica' ? 'active' : ''}
                        onClick={() => {
                          setTryOnType('replica');
                          setTryOnError('');
                          setTryOnStatus((current) => (current === 'error' ? 'idle' : current));
                        }}
                      >
                        <span>{t('tryon.modeReplicaTitle')}</span>
                        <strong>{t('tryon.modeReplicaName')}</strong>
                        <em>{t('tryon.modeReplicaCopy')}</em>
                      </button>
                    </div>
                    {!canBuildCartFit ? (
                      <section className="ct-tryon-cart-explainer">
                        <div>
                          <p className="ct-kicker">{t('tryon.cartUnavailableKicker')}</p>
                          <h3>{t('tryon.cartUnavailableTitle')}</h3>
                          <p>{t('tryon.cartUnavailableCopy')}</p>
                        </div>
                        <button type="button" className="ct-detail-ai-button secondary" onClick={findCartProductsFromTryOn}>
                          <i className={aiStatus === 'loading' ? 'ph ph-spinner-gap' : 'ph ph-magnifying-glass'}></i>
                          {aiStatus === 'loading' ? t('tryon.cartUnavailableLoading') : t('tryon.cartUnavailableCta')}
                        </button>
                      </section>
                    ) : null}
                    {tryOnType === 'selected_products' ? (
                      <section className="ct-tryon-shop-builder">
                        <div className="ct-tryon-shop-head">
                          <div>
                            <h3>{t('tryon.shopBuilderTitle')}</h3>
                            <p>{t('tryon.shopBuilderCopy')}</p>
                          </div>
                          <div>
                            <span>{t('tryon.selectedCount', { count: pickedProducts.length })}</span>
                            <strong>
                              {pickedTotal
                                ? formatMoney(pickedTotal.amount, pickedTotal.currency)
                                : t('tryon.totalUnavailable')}
                            </strong>
                          </div>
                        </div>
                        <div className="ct-tryon-cart-presets" aria-label={t('tryon.cartPresetLabel')}>
                          <button
                            type="button"
                            className={activeCartPreset === 'closest' ? 'active' : ''}
                            onClick={() => applyCartPreset('closest')}
                            aria-pressed={activeCartPreset === 'closest'}
                          >
                            <i className="ph ph-sparkle"></i>
                            {t('tryon.cartPresetClosest')}
                          </button>
                          <button
                            type="button"
                            className={activeCartPreset === 'luxury' ? 'active' : ''}
                            onClick={() => applyCartPreset('luxury')}
                            aria-pressed={activeCartPreset === 'luxury'}
                          >
                            <i className="ph ph-crown"></i>
                            {t('tryon.cartPresetLuxury')}
                          </button>
                          <button
                            type="button"
                            className={activeCartPreset === 'budget' ? 'active' : ''}
                            onClick={() => applyCartPreset('budget')}
                            aria-pressed={activeCartPreset === 'budget'}
                          >
                            <i className="ph ph-piggy-bank"></i>
                            {t('tryon.cartPresetBudget')}
                          </button>
                        </div>
                        <div className="ct-tryon-shop-items">
                          {(aiMatch?.items || []).map((item) => {
                            const candidates = candidatePoolForItem(item);
                            return (
                              <article className="ct-tryon-shop-item" key={item.id}>
                                <div>
                                  <strong>{item.category}</strong>
                                  <span>{selectedProductByItem[item.id] ? t('tryon.shopSelected') : t('tryon.shopOriginalFallback')}</span>
                                </div>
                                {candidates.length === 0 ? (
                                  <p>{t('tryon.shopNoProducts')}</p>
                                ) : (
                                  <div className="ct-tryon-shop-products">
                                    {candidates.map((candidate) => {
                                      const isSelected = String(selectedProductByItem[item.id]) === String(candidate.id);
                                      return (
                                        <div
                                          className={`ct-tryon-shop-product${isSelected ? ' active' : ''}`}
                                          key={`${item.id}-${candidate.id}`}
                                        >
                                          <button
                                            type="button"
                                            className="ct-tryon-shop-check"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              toggleSelectedProduct(item.id, candidate.id);
                                            }}
                                            aria-label={isSelected ? t('tryon.shopSelected') : t('tryon.shopOriginalFallback')}
                                          >
                                            <i className={isSelected ? 'ph ph-check' : 'ph ph-plus'}></i>
                                          </button>
                                          <button
                                            type="button"
                                            className="ct-tryon-shop-preview"
                                            onClick={() => openTryOnProductInfo(item, candidate)}
                                          >
                                            <span className="ct-tryon-shop-image">
                                              <img
                                                src={productImageUrl(candidate.image_url)}
                                                alt=""
                                                onError={(event) => { event.currentTarget.closest('.ct-tryon-shop-product')?.remove(); }}
                                              />
                                            </span>
                                            <span className="ct-tryon-shop-copy">
                                              <strong>{candidate.name}</strong>
                                              <em>{candidate.store ? `${candidate.store} · ` : ''}{formatPrice(candidate)}</em>
                                            </span>
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </article>
                            );
                          })}
                        </div>
                        <p className="ct-tryon-shop-note">{t('tryon.shopResponsibleCopy')}</p>
                      </section>
                    ) : null}
                    {canGenerateSelectedProducts ? (
                      <>
                        <div className="ct-tryon-generate-block">
                          <div>
                            <h3>{t('tryon.newPreviewTitle')}</h3>
                            <p>{tryOnType === 'selected_products' ? t('tryon.newPreviewShopCopy') : t('tryon.newPreviewReplicaCopy')}</p>
                          </div>
                          <div className="ct-tryon-picker">
                            {userPhotos.map((photo) => (
                              <button
                                type="button"
                                className={String(photo.id) === String(selectedPhotoId) ? 'active' : ''}
                                onClick={() => setSelectedPhotoId(String(photo.id))}
                                key={photo.id}
                              >
                                <img src={photo.image_url} alt={photo.title || ''} />
                                <span>{photo.title}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        <label className="ct-tryon-email-toggle">
                          <input
                            type="checkbox"
                            checked={notifyEmail}
                            onChange={(event) => setNotifyEmail(event.target.checked)}
                          />
                          <span className="ct-tryon-email-icon">
                            <i className={notifyEmail ? 'ph ph-envelope-simple-open' : 'ph ph-bell-slash'}></i>
                          </span>
                          <span className="ct-tryon-email-copy">
                            <strong>{t('tryon.notifyEmail')}</strong>
                            <small>{notifyEmail ? t('tryon.notifyEmailOnCopy') : t('tryon.notifyEmailOffCopy')}</small>
                          </span>
                        </label>
                        <button type="button" className="ct-detail-ai-button" onClick={runTryOn} disabled={tryOnStatus === 'loading'}>
                          <i className="ph ph-magic-wand"></i>
                          {tryOnStatus === 'loading'
                            ? t('tryon.queueing')
                            : (tryOnType === 'selected_products' ? t('tryon.generateShop') : t('tryon.generateReplica'))}
                        </button>
                      </>
                    ) : (
                      <div className="ct-tryon-selection-empty">
                        <i className="ph ph-hand-tap"></i>
                        <div>
                          <strong>{t('tryon.selectProductsFirstTitle')}</strong>
                          <span>{t('tryon.selectProductsFirstCopy')}</span>
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
                {hasPendingTryOn ? (
                  <div className={`ct-tryon-pending${pendingEmailEnabled ? ' email-on' : ' email-off'}`}>
                    <i className={pendingEmailEnabled ? 'ph ph-envelope-simple-open' : 'ph ph-hourglass-medium'}></i>
                    <strong>{pendingEmailEnabled ? t('tryon.queuedEmailTitle') : t('tryon.queuedTitle')}</strong>
                    <span>{pendingEmailEnabled ? t('tryon.pendingEmailOn') : t('tryon.pendingEmailOff')}</span>
                    <Link to="/profile?tab=tryons" onClick={() => setTryOnOpen(false)}>{t('tryon.openTryons')}</Link>
                  </div>
                ) : null}
                {tryOnStatus === 'error' ? (
                  <div className="ct-tryon-error">
                    {t(`tryon.error.${tryOnError}`) === `tryon.error.${tryOnError}` ? t('tryon.error.generic') : t(`tryon.error.${tryOnError}`)}
                  </div>
                ) : null}
                {tryOnPreview?.image_url ? (
                  <div className="ct-tryon-result">
                    <img src={tryOnPreview.image_url} alt={t('tryon.resultAlt')} />
                    <SocialShareButtons
                      {...getTryOnPreviewShareData()}
                      label={t('tryonShare.button', { celebrity: event?.celebrity_name || t('tryonShare.celebrityFallback') })}
                    />
                  </div>
                ) : null}
              </div>
            )}
          </section>
        </div>
      ) : null}
      {lookShareOpen ? (
        <LookShareCard
          event={event}
          match={aiMatch}
          formatPrice={formatPrice}
          totalLabel={storyTotal ? t('lookShare.total', { total: formatMoney(storyTotal.amount, storyTotal.currency) }) : ''}
          t={t}
          onClose={() => setLookShareOpen(false)}
        />
      ) : null}
      <ProductInfoDialog
        product={tryOnProductInfo}
        formatPrice={formatPrice}
        onClose={() => setTryOnProductInfo(null)}
        t={t}
      />
      {tryOnSuccessPreview ? (
        <aside className="ct-tryon-success-alert" role="status" aria-live="polite">
          <span className="ct-tryon-success-icon">
            <i className="ph ph-check"></i>
          </span>
          <div className="ct-tryon-success-copy">
            <strong>{t('tryon.readyNoticeTitle')}</strong>
            <span>{t('tryon.readyNoticeCopy')}</span>
          </div>
          <div className="ct-tryon-success-actions">
            <Link
              to="/profile?tab=tryons"
              className="ct-detail-ai-button"
              onClick={() => {
                setTryOnOpen(false);
                setTryOnSuccessPreview(null);
              }}
            >
              {t('tryon.readyNoticeCta')}
            </Link>
            <button type="button" className="ct-detail-ai-button secondary" onClick={() => setTryOnSuccessPreview(null)}>
              {t('tryon.readyNoticeStay')}
            </button>
          </div>
        </aside>
      ) : null}
      <PhotoViewer
        open={photoViewerOpen}
        sources={[mainImageUrl]}
        onClose={() => setPhotoViewerOpen(false)}
        t={t}
      />
    </div>
  );
};

export default EventDetail;
